'use client';

import { usePathname } from 'next/navigation';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { LocalJSONStorage } from '~/utils/js';

export const SCROLL_RESTORATION_STORAGE_PREFIX = 'scroll_restoration:';

/** Persists the current window scroll for `Action_RestoreScroll` under this pathname.
 *
 * Call synchronously from a PLP activity-card click before soft navigation to an
 * intercepted PDP modal. Next may reset `window.scrollY` to 0 before the scroll
 * hook's effect cleanup runs, which would otherwise clobber the real PLP offset. */
export function captureScrollForModalRestoration(pathname: string): void {
    if (typeof window === 'undefined') return;

    const store = new LocalJSONStorage<null | { x: number; y: number }>(
        `${SCROLL_RESTORATION_STORAGE_PREFIX}${pathname}`,
        null,
        'sessionStorage',
    );
    store.setValue({
        x: Math.max(window.scrollX, 0),
        y: Math.max(window.scrollY, 0),
    });
}

/** Restores the scroll position onLoad.
 *
 *  Keyed by pathname only — search params are intentionally excluded.
 *  On the PLP, filter changes update the URL via soft routing while the
 *  component stays mounted. Including filter params in the key would re-key
 *  the store on every filter change, and a stored value from a previous
 *  visit at that exact filter combo would yank the user's scroll. The
 *  feature is meant to restore scroll on PDP -> PLP back-nav (full remount),
 *  not to paper over in-page filter state changes. */
export const Action_RestoreScroll: React.FC = () => {
    const pathname = usePathname();

    // Pin the pathname to mount time. When a parallel-route modal intercepts
    // a click on this page (e.g. PLP -> /activiteit/:slug?modal=1), pathname
    // changes to the intercepted route while this component stays mounted in
    // the parent slot. Without pinning, we'd save/restore scroll under the
    // intercepted URL's key — clobbering the real scroll position with 0.
    const [storeKey] = useState(pathname);
    const isOwnPathname = pathname === storeKey;

    // prettier-ignore
    const store = useMemo(() => new LocalJSONStorage<null | { x: number; y: number }>(
        `${SCROLL_RESTORATION_STORAGE_PREFIX}${storeKey}`,
        null,
        'sessionStorage'
    ), [storeKey]);

    /** Ref of the latest restored key (pathname + searchParams) */
    const restoredRef = useRef<string | null>(null);

    useLayoutEffect(() => {
        // Skip while a parallel-route modal is intercepting — searchParams
        // belong to the modal route, not to this page, so don't touch scroll.
        // Clear restoredRef so we re-run the restore step when navigation
        // returns to our own pathname (the previous run's cleanup already
        // saved scroll under the correct storeKey).
        if (!isOwnPathname) {
            restoredRef.current = null;
            return;
        }

        // Only restore scroll position once per unique URL (pathname + searchParams)
        if (restoredRef.current === storeKey) return;
        restoredRef.current = storeKey;

        // Use queueMicrotask to ensure scroll restoration happens after any scrollIntoView calls
        queueMicrotask(() => {
            const value = store.getValue();
            if (value !== null) {
                // Don't restore if the scroll position exceeds the current document height
                const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
                if (value.y <= maxScroll) {
                    window.scrollTo({ behavior: 'instant', left: value.x, top: value.y });
                }
                store.clear();
            }
        });

        return () => {
            const w = {
                x: Math.max(window.scrollX, 0),
                y: Math.max(window.scrollY, 0),
            };
            const prev = store.getValue();
            // Soft-nav to an intercepted modal can zero `window.scrollY` before this
            // cleanup runs. Prefer a value captured on click (see
            // `captureScrollForModalRestoration`) over saving zeros.
            if (w.x === 0 && w.y === 0 && prev !== null && (prev.x > 0 || prev.y > 0)) {
                return;
            }
            store.setValue(w);
        };
    }, [store, storeKey, isOwnPathname]);

    return null;
};
